import React, { useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import TextAlign from '@tiptap/extension-text-align';
import { TextStyle } from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import Highlight from '@tiptap/extension-highlight';
import Placeholder from '@tiptap/extension-placeholder';
import clsx from 'clsx';
import {
  Bold, Italic, Underline as UnderlineIcon, Strikethrough, List, ListOrdered, Quote, AlignLeft, AlignCenter, AlignRight,
  Link as LinkIcon, Undo2, Redo2, Highlighter, Eraser,
} from 'lucide-react';

// The message body editor: the formatting Outlook's compose bar has, nothing more.
const COLORS = ['#201f1e', '#d13438', '#ca5010', '#986f0b', '#498205', '#038387', '#0f6cbd', '#8764b8'];

export default function MailEditor({ value, onChange, placeholder = 'Write your message', autoFocus, minHeight = 220, editorRef, font, fontSize }) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: false, codeBlock: false, code: false, link: false, underline: false }),
      Underline,
      Link.configure({ openOnClick: false, autolink: true, HTMLAttributes: { rel: 'noopener noreferrer', target: '_blank' } }),
      TextAlign.configure({ types: ['paragraph'] }),
      TextStyle, Color, Highlight.configure({ multicolor: false }),
      Placeholder.configure({ placeholder }),
    ],
    content: value || '',
    autofocus: autoFocus ? 'start' : false,
    onUpdate: ({ editor: e }) => onChange && onChange(e.getHTML()),
  });
  // A new reply or draft replaces the content; typing does not.
  useEffect(() => { if (editor && value !== undefined && value !== editor.getHTML() && !editor.isFocused) editor.commands.setContent(value || '', false); }, [editor, value]);
  if (editorRef) editorRef.current = editor;
  if (!editor) return null;
  const B = ({ on, label, act, children }) => (
    <button type="button" title={label} aria-label={label} aria-pressed={!!on} onMouseDown={(e) => { e.preventDefault(); act(); }}
      className={clsx('w-8 h-8 rounded-md flex items-center justify-center text-slate-600 hover:bg-slate-100 hover:text-slate-900', on && 'bg-sky-100 text-[#0f6cbd]')}>
      {children}
    </button>
  );
  const link = () => {
    const prev = editor.getAttributes('link').href || '';
    const url = window.prompt('Link address', prev || 'https://');
    if (url === null) return;
    if (!url || url === 'https://') { editor.chain().focus().unsetLink().run(); return; }
    if (!/^(https?:|mailto:)/i.test(url)) return;
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };
  return (
    <div className="flex flex-col min-h-0">
      <div className="flex flex-wrap items-center gap-0.5 border-b border-slate-200 px-1 py-1 bg-white sticky top-0 z-[1]" role="toolbar" aria-label="Formatting">
        <B label="Bold (Ctrl+B)" on={editor.isActive('bold')} act={() => editor.chain().focus().toggleBold().run()}><Bold size={15} /></B>
        <B label="Italic (Ctrl+I)" on={editor.isActive('italic')} act={() => editor.chain().focus().toggleItalic().run()}><Italic size={15} /></B>
        <B label="Underline (Ctrl+U)" on={editor.isActive('underline')} act={() => editor.chain().focus().toggleUnderline().run()}><UnderlineIcon size={15} /></B>
        <B label="Strikethrough" on={editor.isActive('strike')} act={() => editor.chain().focus().toggleStrike().run()}><Strikethrough size={15} /></B>
        <B label="Highlight" on={editor.isActive('highlight')} act={() => editor.chain().focus().toggleHighlight().run()}><Highlighter size={15} /></B>
        <span className="flex items-center gap-0.5 mx-1" role="group" aria-label="Font colour">
          {COLORS.map((c) => (
            <button key={c} type="button" title={`Colour ${c}`} aria-label={`Font colour ${c}`} onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().setColor(c).run(); }}
              className="w-4 h-4 rounded-full ring-1 ring-black/10 hover:scale-110 transition-transform" style={{ background: c }} />
          ))}
        </span>
        <span className="w-px h-5 bg-slate-200 mx-1" aria-hidden />
        <B label="Bulleted list" on={editor.isActive('bulletList')} act={() => editor.chain().focus().toggleBulletList().run()}><List size={15} /></B>
        <B label="Numbered list" on={editor.isActive('orderedList')} act={() => editor.chain().focus().toggleOrderedList().run()}><ListOrdered size={15} /></B>
        <B label="Quote" on={editor.isActive('blockquote')} act={() => editor.chain().focus().toggleBlockquote().run()}><Quote size={15} /></B>
        <B label="Align left" on={editor.isActive({ textAlign: 'left' })} act={() => editor.chain().focus().setTextAlign('left').run()}><AlignLeft size={15} /></B>
        <B label="Centre" on={editor.isActive({ textAlign: 'center' })} act={() => editor.chain().focus().setTextAlign('center').run()}><AlignCenter size={15} /></B>
        <B label="Align right" on={editor.isActive({ textAlign: 'right' })} act={() => editor.chain().focus().setTextAlign('right').run()}><AlignRight size={15} /></B>
        <B label="Link" on={editor.isActive('link')} act={link}><LinkIcon size={15} /></B>
        <B label="Clear formatting" act={() => editor.chain().focus().unsetAllMarks().clearNodes().run()}><Eraser size={15} /></B>
        <span className="w-px h-5 bg-slate-200 mx-1" aria-hidden />
        <B label="Undo (Ctrl+Z)" act={() => editor.chain().focus().undo().run()}><Undo2 size={15} /></B>
        <B label="Redo (Ctrl+Y)" act={() => editor.chain().focus().redo().run()}><Redo2 size={15} /></B>
      </div>
      <EditorContent editor={editor} className="mail-editor prose prose-sm max-w-none px-4 py-3 text-[14px] text-slate-900 focus-within:outline-none" style={{ minHeight, ...(font ? { '--mail-font': font } : {}), ...(fontSize ? { '--mail-size': `${fontSize}px` } : {}) }} />
    </div>
  );
}
